import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, PencilIcon, TrashIcon, HeartIcon, CheckCircleIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../hooks/useSubscription';
import UpgradePrompt from '../components/UpgradePrompt';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8005';

// Toast notification component
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 5000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const bgColor = type === 'success' ? 'bg-green-50 border-green-200' : 
                  type === 'error' ? 'bg-red-50 border-red-200' : 'bg-blue-50 border-blue-200';
  const textColor = type === 'success' ? 'text-green-800' : 
                    type === 'error' ? 'text-red-800' : 'text-blue-800';
  const icon = type === 'success' ? '✓' : type === 'error' ? '✕' : 'ℹ';

  return (
    <div className={`fixed top-4 right-4 z-50 max-w-md border ${bgColor} ${textColor} px-6 py-4 rounded-lg shadow-lg animate-slide-in`}>
      <div className="flex items-start">
        <span className="text-xl mr-3">{icon}</span>
        <p className="flex-1">{message}</p>
        <button onClick={onClose} className="ml-4 text-gray-400 hover:text-gray-600">×</button>
      </div>
    </div>
  );
};

export default function Recipients() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { subscription, usage, hasReachedLimit, checkLimit, incrementUsage, refreshSubscription } = useSubscription();
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRecipient, setEditingRecipient] = useState(null);
  const [toast, setToast] = useState(null);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [sendingInvite, setSendingInvite] = useState(null); // Track which recipient is being invited
  const [formData, setFormData] = useState({
    name: '',
    relationship: '',
    email: '',
    phone_number: '',
    country_code: '+1',
    pin_code: ''
  });
  const [phoneError, setPhoneError] = useState('');

  useEffect(() => {
    if (user) {
      fetchRecipients();
    }
  }, [user]);

  const fetchRecipients = async () => {
    if (!user) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/recipients/user/${user.id}`);
      setRecipients(response.data.recipients || []);
    } catch (error) {
      console.error('Failed to fetch recipients:', error);
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message, type = 'info') => {
    setToast({ message, type });
  };

  const validatePhone = (phone, countryCode) => {
    const fullPhone = countryCode + phone;
    // US/Canada: 11 digits (+1XXXXXXXXXX)
    if (countryCode === '+1' && phone.length !== 10) {
      return 'US/Canada numbers must be 10 digits';
    }
    // Basic validation: must be digits only
    if (!/^\d+$/.test(phone)) {
      return 'Phone number must contain only digits';
    }
    return '';
  };

  const getPortalLink = async (recipientId) => {
    try {
      const response = await axios.post(`${API_URL}/recipients/${recipientId}/generate-token`);
      const portalUrl = `${window.location.origin}/portal/${response.data.token}`;
      
      try {
        await navigator.clipboard.writeText(portalUrl);
        showToast('Portal link copied to clipboard! Share it with your loved one.', 'success');
      } catch (clipboardError) {
        prompt('Copy this portal link:', portalUrl);
      }
    } catch (error) {
      showToast('Failed to generate portal link. Please try again.', 'error');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Ensure user is authenticated
    if (!user || !user.id) {
      showToast('Please log in to add recipients', 'error');
      return;
    }
    
    // Validate phone
    const phoneValidationError = validatePhone(formData.phone_number, formData.country_code);
    if (phoneValidationError) {
      setPhoneError(phoneValidationError);
      return;
    }
    setPhoneError('');

    // Check recipient limit before adding
    if (!editingRecipient && hasReachedLimit('recipients_added')) {
      setShowUpgradePrompt(true);
      return;
    }

    try {
      const fullPhone = formData.country_code + formData.phone_number;
      
      const payload = {
        ...formData,
        phone_number: fullPhone,
        creator_id: user.id
      };
      delete payload.country_code;
      
      if (editingRecipient) {
        await axios.put(`${API_URL}/recipients/${editingRecipient._id}`, payload);
        showToast(`${formData.name} has been updated successfully!`, 'success');
      } else {
        await axios.post(`${API_URL}/recipients`, payload);
        // Increment recipient usage counter
        await incrementUsage('recipients', 1);
        showToast(`${formData.name} has been added! You can now send them an invitation.`, 'success');
      }
      
      setShowModal(false);
      setEditingRecipient(null);
      setFormData({
        name: '',
        relationship: '',
        email: '',
        phone_number: '',
        country_code: '+1'
      });
      setPhoneError('');
      fetchRecipients();
      refreshSubscription();
    } catch (error) {
      showToast('Failed to save. Please check all fields and try again.', 'error');
    }
  };

  const handleEdit = (recipient) => {
    setEditingRecipient(recipient);
    setFormData({
      name: recipient.name,
      relationship: recipient.relationship || '',
      email: recipient.email || ''
    });
    setShowModal(true);
  };

  const sendInvitation = async (recipientId) => {
    setSendingInvite(recipientId);
    try {
      const response = await axios.post(`${API_URL}/recipients/${recipientId}/send-invitation`);
      showToast(
        `Invitation sent successfully! They'll receive a WhatsApp message with instructions to activate.`,
        'success'
      );
      fetchRecipients();
    } catch (error) {
      console.error('Send invitation error:', error);
      
      // Check if error is about missing voice clone
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('clone your voice')) {
        showToast(
          error.response.data.detail,
          'error'
        );
        // Redirect to voice clone page after 3 seconds
        setTimeout(() => {
          window.location.href = '/voice-clone';
        }, 3000);
      } else {
        showToast(
          'Unable to send invitation. Please ensure the recipient has a valid phone number.',
          'error'
        );
      }
    } finally {
      setSendingInvite(null);
    }
  };

  const handleDelete = async (recipientId) => {
    const recipient = recipients.find(r => r._id === recipientId);
    if (!window.confirm(`Remove ${recipient?.name || 'this person'} from your recipients?`)) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/recipients/${recipientId}`);
      setRecipients(recipients.filter(r => r._id !== recipientId));
      showToast('Recipient removed successfully.', 'success');
    } catch (error) {
      showToast('Failed to remove recipient. Please try again.', 'error');
    }
  };


  const handleAddRecipient = async () => {
    // Check limit before opening modal
    const limitCheck = await checkLimit('recipients');
    if (!limitCheck.allowed) {
      setShowUpgradePrompt(true);
      return;
    }
    
    setEditingRecipient(null);
    setFormData({
      name: '',
      relationship: '',
      email: '',
      phone_number: '',
      country_code: '+1'
    });
    setPhoneError('');
    setShowModal(true);
  };

  return (
    <div className="max-w-5xl mx-auto">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      
      {/* Upgrade Prompt Modal */}
      <UpgradePrompt
        isOpen={showUpgradePrompt}
        onClose={() => setShowUpgradePrompt(false)}
        limitType="recipients"
        currentTier={subscription?.tier || 'free'}
        currentUsage={usage?.usage?.recipients?.current || 0}
        limit={usage?.usage?.recipients?.limit || 2}
      />
      
      {/* Subscription Usage Banner */}
      {usage && !loading && (
        <div className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">
                Current Plan: <span className="text-blue-600 capitalize">{subscription?.tier || 'Free'}</span>
              </h3>
              <p className="text-xs text-gray-600 mt-1">
                Recipients: {usage.usage.recipients.current}/{usage.usage.recipients.limit === -1 ? '∞' : usage.usage.recipients.limit}
              </p>
            </div>
            {hasReachedLimit('recipients') && (
              <button
                onClick={() => setShowUpgradePrompt(true)}
                className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-semibold rounded-lg hover:shadow-lg transition-all"
              >
                Upgrade Now
              </button>
            )}
          </div>
          {usage.usage.recipients.limit !== -1 && (
            <div className="mt-3">
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all ${
                    usage.usage.recipients.percentage >= 100
                      ? 'bg-red-500'
                      : usage.usage.recipients.percentage >= 80
                      ? 'bg-yellow-500'
                      : 'bg-green-500'
                  }`}
                  style={{ width: `${Math.min(100, usage.usage.recipients.percentage)}%` }}
                />
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-8 mb-8 border border-purple-100">
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0">
            <HeartIcon className="h-12 w-12 text-purple-600" />
          </div>
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-900 mb-3">Share Your Voice with Loved Ones</h1>
            <p className="text-lg text-gray-700 mb-4">
              Stay connected with the people who matter most. Add your family and friends below, 
              and they'll be able to receive personalized voice messages from you anytime they need to hear from you.
            </p>
            <div className="bg-white/60 backdrop-blur rounded-lg p-4 mb-4">
              <h3 className="font-semibold text-gray-900 mb-2">How it works:</h3>
              <ol className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start">
                  <span className="flex-shrink-0 w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center text-xs mr-3 mt-0.5">1</span>
                  <span>Add your loved one's name and WhatsApp number</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center text-xs mr-3 mt-0.5">2</span>
                  <span>Send them an invitation via WhatsApp</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center text-xs mr-3 mt-0.5">3</span>
                  <span>They reply "YES" to activate and can reach out to you anytime</span>
                </li>
              </ol>
            </div>
            <button
              onClick={handleAddRecipient}
              className="inline-flex items-center px-6 py-3 border border-transparent rounded-lg shadow-sm text-base font-medium text-white bg-purple-600 hover:bg-purple-700 transition-colors"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Add a Loved One
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg">
        <div className="divide-y divide-gray-200">
          {loading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              <p className="text-gray-600 mt-2">Loading recipients...</p>
            </div>
          ) : recipients.length === 0 ? (
            <div className="p-12 text-center">
              <p className="text-gray-500">No recipients added yet</p>
              <p className="text-sm text-gray-400 mt-2">
                Add recipients to start sending them messages
              </p>
            </div>
          ) : (
            recipients.map((recipient) => (
              <div key={recipient._id} className="p-6 hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg font-medium text-gray-900">
                      {recipient.name}
                    </h3>
                    <div className="mt-1 space-y-1">
                      {recipient.relationship && (
                        <p className="text-sm text-gray-600">
                          Relationship: <span className="font-medium">{recipient.relationship}</span>
                        </p>
                      )}
                      {recipient.email && (
                        <p className="text-sm text-gray-600">
                          Email: {recipient.email}
                        </p>
                      )}
                      {recipient.phone_number && (
                        <p className="text-sm text-gray-600">
                          Phone: {recipient.phone_number}
                        </p>
                      )}
                      {recipient.pin_code && (
                        <p className="text-sm text-gray-600">
                          PIN Code: <span className="font-mono font-medium">{recipient.pin_code}</span>
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="ml-4 flex items-center space-x-2">
                    {recipient.activation_status === 'active' && (
                      <span className="px-3 py-1 text-xs bg-green-100 text-green-800 rounded-full font-medium">
                        ✓ Active
                      </span>
                    )}
                    {recipient.activation_status === 'pending' && (
                      <span className="px-3 py-1 text-xs bg-yellow-100 text-yellow-800 rounded-full font-medium">
                        ⏳ Pending
                      </span>
                    )}
                    {recipient.activation_status === 'paused' && (
                      <span className="px-3 py-1 text-xs bg-gray-100 text-gray-800 rounded-full font-medium">
                        ⏸️ Paused
                      </span>
                    )}
                    <button
                      onClick={() => handleEdit(recipient)}
                      className="p-2 text-gray-600 hover:text-gray-700 hover:bg-gray-100 rounded-full"
                      title="Edit"
                    >
                      <PencilIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => sendInvitation(recipient._id)}
                      disabled={sendingInvite === recipient._id}
                      className={`px-3 py-1 text-sm rounded-lg transition-all ${
                        sendingInvite === recipient._id
                          ? 'bg-gray-400 cursor-not-allowed'
                          : 'bg-blue-600 hover:bg-blue-700 text-white'
                      }`}
                    >
                      {sendingInvite === recipient._id ? (
                        <>
                          <span className="inline-block animate-spin mr-1">⏳</span>
                          Sending...
                        </>
                      ) : (
                        <>📱 Send Invite</>
                      )}
                    </button>
                    <button
                      onClick={() => handleDelete(recipient._id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-all"
                      title="Delete"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/recipients/${recipient._id}`);
                      }}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-all flex items-center gap-2"
                    >
                      View Messages
                      <ArrowRightIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              {editingRecipient ? 'Edit Recipient' : 'Add Recipient'}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="e.g., Sarah Johnson"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Relationship
                </label>
                <select
                  value={formData.relationship}
                  onChange={(e) => setFormData({ ...formData, relationship: e.target.value })}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                >
                  <option value="">Select relationship</option>
                  <option value="daughter">Daughter</option>
                  <option value="son">Son</option>
                  <option value="spouse">Spouse</option>
                  <option value="grandchild">Grandchild</option>
                  <option value="friend">Friend</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  WhatsApp Phone Number *
                </label>
                <div className="flex space-x-2">
                  <select
                    value={formData.country_code}
                    onChange={(e) => {
                      setFormData({ ...formData, country_code: e.target.value });
                      setPhoneError('');
                    }}
                    className="w-24 border-gray-300 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 text-sm"
                  >
                    <option value="+1">🇺🇸 +1</option>
                    <option value="+44">🇬🇧 +44</option>
                    <option value="+91">🇮🇳 +91</option>
                    <option value="+86">🇨🇳 +86</option>
                    <option value="+81">🇯🇵 +81</option>
                    <option value="+49">🇩🇪 +49</option>
                    <option value="+33">🇫🇷 +33</option>
                    <option value="+39">🇮🇹 +39</option>
                    <option value="+34">🇪🇸 +34</option>
                    <option value="+61">🇦🇺 +61</option>
                  </select>
                  <input
                    type="tel"
                    required
                    value={formData.phone_number}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '');
                      setFormData({ ...formData, phone_number: value });
                      setPhoneError('');
                    }}
                    className={`flex-1 border-gray-300 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 text-sm ${
                      phoneError ? 'border-red-300' : ''
                    }`}
                    placeholder="3122703627"
                    maxLength="15"
                  />
                </div>
                {phoneError && (
                  <p className="mt-1 text-xs text-red-600">{phoneError}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  {formData.country_code === '+1' 
                    ? 'Enter 10 digits (e.g., 3122703627 for US/Canada)'
                    : 'Enter phone number without country code'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Email *
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="email@example.com"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Required to send WhatsApp setup instructions
                </p>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingRecipient(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2.5 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 transition-colors"
                >
                  {editingRecipient ? 'Update' : 'Add'} Loved One
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
